import React, { useState, useEffect } from 'react';
import { UseFormRegister, FieldErrors, Controller, Control, UseFormTrigger, UseFormSetValue, useWatch } from 'react-hook-form';
import { User, AlertTriangle, Globe } from 'lucide-react';
import { formatCPF, canRegisterWithoutCPF } from '../../utils/cpfFormatter';
import {
  countryOptions,
  formatForeignPhone,
  isCpfRequired,
  normalizePassport,
  passportError,
  whatsappNumber,
} from '../../utils/foreigner';
import { effectivePersonRoles } from '../PersonDetailModal/personModalRules';
import { joinFullName, splitFullName, validateFullName, sanitizePersonName } from '../../utils/fullName';
import { DateInput } from '../ui/DateInput';
import { useKeycloak } from '../../contexts/KeycloakContext';
import toast from 'react-hot-toast';
import { PersonRegistrationFieldConfig } from '../../types/customer/base';
import { getOrderedFields } from '../../utils/fieldConfigUtils';

export interface PersonalInfoFormData {
  full_name?: string; // virtual field used only in the UI
  first_name: string;
  last_name: string;
  cpf?: string;
  rg?: string;
  email?: string;
  phone?: string;
  birth_date?: string;
  is_minor?: boolean;
  /** The declaration that swaps the identity rules over — see utils/foreigner.ts. */
  is_foreigner?: boolean;
  passport_number?: string;
  /** ISO 3166-1 alpha-2, never a display name. */
  country?: string;
}

// ── field-config helpers ─────────────────────────────────────────────────────

function isFieldVisible(key: string, fieldConfig?: PersonRegistrationFieldConfig | null): boolean {
  const def = fieldConfig?.standard_fields?.[key];
  return def ? def.enabled : true; // default = visible
}

function isFieldRequired(key: string, fieldConfig?: PersonRegistrationFieldConfig | null, fallback = false): boolean {
  const def = fieldConfig?.standard_fields?.[key];
  return def ? def.required : fallback;
}

function fieldLabel(key: string, defaultLabel: string, fieldConfig?: PersonRegistrationFieldConfig | null): string {
  return fieldConfig?.standard_fields?.[key]?.label || defaultLabel;
}

interface PersonalInfoSectionProps {
  register: UseFormRegister<any>;
  control: Control<any>;
  trigger: UseFormTrigger<any>;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors<PersonalInfoFormData>;
  disabled?: boolean;
  cpfRequired?: boolean;
  darkMode?: boolean;
  fieldConfig?: PersonRegistrationFieldConfig | null;
  /** Needed to read the caller's customer-scoped roles — see the CPF waiver gate below. */
  customerId?: string;
}

export const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  register,
  control,
  trigger,
  setValue,
  errors,
  disabled = false,
  cpfRequired = true,
  darkMode = false,
  fieldConfig,
  customerId,
}) => {
  // Sync the virtual full_name field whenever first_name / last_name are changed
  // from outside (e.g. form reset or setValue calls in parent)
  const watchedFirst = useWatch({ control, name: 'first_name', defaultValue: '' });
  const watchedLast  = useWatch({ control, name: 'last_name',  defaultValue: '' });
  useEffect(() => {
    const joined = joinFullName(watchedFirst || '', watchedLast || '');
    setValue('full_name', joined, { shouldValidate: false });
  }, [watchedFirst, watchedLast]); // eslint-disable-line react-hooks/exhaustive-deps

  // Who may waive the CPF requirement — one rule, shared with ResidentModal and
  // VisitorRegistrationModal (canRegisterWithoutCPF). It used to be a role list written out here,
  // which is how a porteiro could register a morador with no CPF while the síndico could not.
  //
  // Read from BOTH role stores. `roles` is the Keycloak token; a síndico's role is normally an
  // entry in user_customer_assignments and reaches us only through getRolesForCustomer, so a gate
  // that reads the token alone sees nobody — which is why the waiver stayed invisible even after
  // the rule was fixed. See effectivePersonRoles.
  const { isSuperAdmin, roles, getRolesForCustomer } = useKeycloak();
  const effectiveRoles = effectivePersonRoles(
    customerId ? getRolesForCustomer(customerId) : [],
    roles,
  );
  const isAdmin = canRegisterWithoutCPF(effectiveRoles, isSuperAdmin);

  // Admin checkbox to allow registration without CPF
  const [allowWithoutCPF, setAllowWithoutCPF] = useState(false);

  // "É estrangeiro": a foreigner is identified by a passport, not a CPF. See utils/foreigner.ts.
  const isForeigner = !!useWatch({ control, name: 'is_foreigner' });

  // Determine if CPF is actually required (fieldConfig overrides cpfRequired prop)
  const cpfRequiredFromConfig = isFieldRequired('cpf', fieldConfig, cpfRequired);
  const cpfRule = {
    requiredByConfig: cpfRequiredFromConfig,
    waiverAllowed: isAdmin,
    waiverChecked: allowWithoutCPF,
    isForeigner,
  };
  const isCPFRequired = isCpfRequired(cpfRule);

  // Watch phone value for WhatsApp button
  const phone = useWatch({ control, name: 'phone' });

  return (
    <div className="space-y-3">
      {/* Section Header */}
      <div className={`flex items-center gap-2 pb-2 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
        <User className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
        <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Dados Pessoais</h3>
      </div>

      {/* Form Grid — Name fields always first, then configurable fields in admin-defined order */}
      <div className="grid grid-cols-2 gap-3">
        {/* Hidden fields that carry first_name / last_name to the form submit */}
        <input type="hidden" {...register('first_name')} />
        <input type="hidden" {...register('last_name')} />

        {/* Full Name — single combined field */}
        <div className="col-span-2">
          <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Nome Completo <span className="text-red-500">*</span>
          </label>
          <Controller
            name="full_name"
            control={control}
            defaultValue=""
            rules={{ validate: (v) => validateFullName(v ?? '') ?? true }}
            render={({ field }) => (
              <input
                type="text"
                {...field}
                onChange={(e) => {
                  // Letters only, as typed — see sanitizePersonName.
                  const value = sanitizePersonName(e.target.value);
                  field.onChange(value);
                  const { first_name, last_name } = splitFullName(value);
                  setValue('first_name', first_name, { shouldValidate: false });
                  setValue('last_name', last_name, { shouldValidate: false });
                }}
                disabled={disabled}
                placeholder="Ex: João Silva"
                className={`w-full py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  (errors as any).full_name ? 'border-red-400' : darkMode ? 'border-gray-600' : 'border border-gray-300'
                } ${darkMode ? 'bg-gray-800 text-white placeholder-gray-500' : ''} ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
              />
            )}
          />
          {(errors as any).full_name && (
            <p className="text-red-500 text-xs mt-1">{(errors as any).full_name.message as string}</p>
          )}
        </div>

        {/* É estrangeiro — placed before the identity fields because it is what decides which
            of them apply: a foreigner is identified by a passport, not a CPF. */}
        <div className="col-span-2">
          <Controller
            name="is_foreigner"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <div
                className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${
                  field.value
                    ? darkMode
                      ? 'border-primary-700 bg-primary-900/20'
                      : 'border-primary-200 bg-primary-50'
                    : darkMode
                    ? 'border-gray-700 bg-gray-800/40'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <Globe className={`h-4 w-4 flex-shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  Estrangeiro
                </span>
                {/* The current state in words — a lone switch does not say which way is on. */}
                <span
                  className={`ml-auto text-xs font-bold tracking-wide ${
                    field.value ? 'text-emerald-600' : darkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}
                >
                  {field.value ? 'SIM' : 'NÃO'}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!field.value}
                  aria-label="Estrangeiro"
                  disabled={disabled}
                  onClick={() => {
                    const next = !field.value;
                    field.onChange(next);
                    // The CPF field is about to disappear. A hidden input that still holds a
                    // half-typed CPF fails validation where nobody can see the error, and a
                    // complete one is saved to a record whose operator was told it had none.
                    if (next) setValue('cpf', '', { shouldValidate: false });
                    // Only the CPF: re-validating the passport here puts "obrigatório" under a
                    // field the operator has not reached yet.
                    setTimeout(() => trigger('cpf'), 0);
                  }}
                  className={`h-5 w-9 flex-shrink-0 rounded-full transition-colors ${
                    field.value ? 'bg-emerald-500' : darkMode ? 'bg-gray-600' : 'bg-gray-300'
                  } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`block h-4 w-4 rounded-full bg-white transition-transform ${
                      field.value ? 'translate-x-[1.125rem]' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            )}
          />
        </div>

        {isForeigner && (
          <>
            <div>
              <label
                htmlFor="passport_number"
                className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
              >
                Número do Passaporte <span className="text-red-500">*</span>
              </label>
              <Controller
                name="passport_number"
                control={control}
                defaultValue=""
                rules={{ validate: (v) => passportError(isForeigner, v) ?? true }}
                render={({ field }) => (
                  <input
                    id="passport_number"
                    type="text"
                    {...field}
                    value={field.value || ''}
                    // Normalised as it is typed, the way the column stores it: the number comes
                    // off a physical document and the spacing is the transcriber's, not the
                    // passport's.
                    onChange={(e) => field.onChange(normalizePassport(e.target.value))}
                    disabled={disabled}
                    placeholder="AB123456"
                    maxLength={20}
                    className={`w-full py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      (errors as any).passport_number
                        ? 'border border-red-400'
                        : darkMode
                        ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                        : 'border border-gray-300'
                    } ${darkMode ? 'bg-gray-800 text-white placeholder-gray-500' : ''} ${
                      disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''
                    }`}
                  />
                )}
              />
              {(errors as any).passport_number && (
                <p className="text-red-500 text-xs mt-1">
                  {(errors as any).passport_number.message as string}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="country"
                className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
              >
                País
              </label>
              <select
                id="country"
                {...register('country')}
                disabled={disabled}
                className={`w-full py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border border-gray-300'
                } ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
              >
                <option value="">Selecione…</option>
                {countryOptions().map(({ code, name }) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* Configurable fields in admin-defined order */}
        {getOrderedFields(fieldConfig ?? {})
          .filter(item => item.enabled)
          .map(item => {
            const cls = item.col_span === 2 ? 'col-span-2' : undefined;
            if (!item.isCustom) {
              // Standard field
              switch (item.key) {
                case 'cpf':
                  // A foreigner is identified by the passport above; the CPF field is not just
                  // optional here, it is not asked for at all.
                  if (isForeigner) return null;
                  return (
                    <div key="cpf" className={cls}>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {fieldLabel('cpf', 'CPF', fieldConfig)} {isCPFRequired && <span className="text-red-500">*</span>}
                      </label>
                      <Controller
                        name="cpf"
                        control={control}
                        defaultValue=""
                        rules={{
                          validate: (value) => {
                            if (isCpfRequired(cpfRule) && (!value || value.trim() === '')) {
                              return 'CPF é obrigatório';
                            }
                            return true;
                          }
                        }}
                        render={({ field }) => (
                          <input
                            type="text"
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => {
                              const formatted = formatCPF(e.target.value);
                              field.onChange(formatted);
                            }}
                            disabled={disabled}
                            className={`w-full py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                              darkMode
                                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                                : 'border border-gray-300'
                            } ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
                            placeholder="000.000.000-00"
                            maxLength={14}
                          />
                        )}
                      />
                      {errors.cpf && (
                        <p className="text-red-500 text-xs mt-1">{errors.cpf.message as string}</p>
                      )}
                      {isAdmin && (
                        <>
                          <label className="flex items-center cursor-pointer mt-2">
                            <input
                              type="checkbox"
                              checked={allowWithoutCPF}
                              onChange={(e) => {
                                setAllowWithoutCPF(e.target.checked);
                                setTimeout(() => trigger('cpf'), 0);
                              }}
                              className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Permitir cadastro sem CPF
                            </span>
                          </label>
                          {allowWithoutCPF && (
                            <div className={`mt-2 p-2 rounded-md ${darkMode ? 'bg-amber-900/30 border border-amber-700' : 'bg-amber-50 border border-amber-200'}`}>
                              <div className="flex items-start">
                                <AlertTriangle className={`h-4 w-4 mt-0.5 mr-2 flex-shrink-0 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                                <p className={`text-xs ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                                  <strong>Aviso:</strong> Cadastrar pessoa sem CPF pode dificultar a identificação futura e impedir a verificação de duplicatas.
                                </p>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                case 'phone':
                  return (
                    <div key="phone" className={cls}>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {fieldLabel('phone', 'Telefone', fieldConfig)}{isFieldRequired('phone', fieldConfig) && <span className="text-red-500"> *</span>}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="tel"
                          {...register('phone', { required: isFieldRequired('phone', fieldConfig) ? 'Telefone é obrigatório' : false })}
                          disabled={disabled}
                          className={`flex-1 py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                            darkMode
                              ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                              : 'border border-gray-300'
                          } ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
                          placeholder={isForeigner ? '+58 412 1234567' : '(11) 99999-9999'}
                          onChange={(e) => {
                            // The Brazilian mask slices the first two digits into a DDD, so a
                            // foreign number comes out as "(58) 41212-34567" — undialable. The
                            // leading "+" is also what stops condo-manager masking it again on
                            // the way back out (utils.NormalizePhone).
                            if (isForeigner) {
                              setValue('phone', formatForeignPhone(e.target.value), { shouldValidate: false });
                              return;
                            }
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                            let masked = digits;
                            if (digits.length > 2) masked = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
                            if (digits.length > 7) masked = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
                            setValue('phone', masked, { shouldValidate: false });
                          }}
                        />
                        {phone && phone.replace(/\D/g, '').length >= 10 && (
                          <button
                            type="button"
                            onClick={() => {
                              // 55 in front of a foreign number dials a Brazilian one that does
                              // not exist; the typed country code is already the prefix.
                              window.open(`https://wa.me/${whatsappNumber(phone, isForeigner)}`, '_blank');
                            }}
                            className={`flex-shrink-0 px-3 py-2 rounded-lg transition-colors flex items-center gap-1 ${
                              darkMode
                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                : 'bg-green-500 hover:bg-green-600 text-white'
                            }`}
                            title="Abrir WhatsApp"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                case 'email':
                  return (
                    <div key="email" className={cls}>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {fieldLabel('email', 'Email', fieldConfig)}{isFieldRequired('email', fieldConfig) && <span className="text-red-500"> *</span>}
                      </label>
                      <input
                        type="email"
                        {...register('email', { required: isFieldRequired('email', fieldConfig) ? 'Email é obrigatório' : false })}
                        disabled={disabled}
                        className={`w-full py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                          darkMode
                            ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                            : 'border border-gray-300'
                        } ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
                        placeholder="email@exemplo.com"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-xs mt-1">{errors.email.message as string}</p>
                      )}
                    </div>
                  );
                case 'date_of_birth':
                  return (
                    <div key="date_of_birth" className={cls}>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {fieldLabel('date_of_birth', 'Data de Nascimento', fieldConfig)}{isFieldRequired('date_of_birth', fieldConfig) && <span className="text-red-500"> *</span>}
                      </label>
                      <Controller
                        name="birth_date"
                        control={control}
                        defaultValue=""
                        rules={{ required: isFieldRequired('date_of_birth', fieldConfig) ? 'Data de nascimento é obrigatória' : false }}
                        render={({ field }) => (
                          <DateInput
                            value={field.value || ''}
                            onChange={field.onChange}
                            disabled={disabled}
                            error={!!errors.birth_date}
                            className={`w-full py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                              darkMode
                                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                                : 'border border-gray-300'
                            } ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
                          />
                        )}
                      />
                      {errors.birth_date && (
                        <p className="text-red-500 text-xs mt-1">{errors.birth_date.message as string}</p>
                      )}
                      <Controller
                        name="is_minor"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <label className="flex items-center cursor-pointer mt-2">
                            <input
                              type="checkbox"
                              id="is_minor"
                              checked={field.value || false}
                              onChange={(e) => field.onChange(e.target.checked)}
                              disabled={disabled}
                              className={`mr-2 h-4 w-4 rounded focus:ring-2 focus:ring-primary-500 border-gray-300 ${
                                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                              }`}
                            />
                            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              Menor de 18 anos
                            </span>
                          </label>
                        )}
                      />
                    </div>
                  );
                case 'rg':
                  return (
                    <div key="rg" className={cls}>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {fieldLabel('rg', 'RG', fieldConfig)}{isFieldRequired('rg', fieldConfig) && <span className="text-red-500"> *</span>}
                      </label>
                      <input
                        type="text"
                        {...register('rg', { required: isFieldRequired('rg', fieldConfig) ? 'RG é obrigatório' : false })}
                        disabled={disabled}
                        className={`w-full py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                          darkMode
                            ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                            : 'border border-gray-300'
                        } ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
                        placeholder="RG"
                      />
                    </div>
                  );
                default:
                  return null;
              }
            } else {
              // Custom field
              const field = fieldConfig?.custom_fields?.find(f => f.id === item.key);
              if (!field) return null;
              return (
                <div key={field.id} className={cls}>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {field.label}{field.required && <span className="text-red-500"> *</span>}
                  </label>
                  {field.type === 'select' ? (
                    <select
                      {...register(`extra_data.${field.id}`, { required: field.required ? `${field.label} é obrigatório` : false })}
                      disabled={disabled}
                      className={`w-full py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        darkMode
                          ? 'bg-gray-800 border-gray-600 text-white'
                          : 'border border-gray-300'
                      } ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
                    >
                      <option value="">Selecione…</option>
                      {field.options?.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : field.type === 'textarea' ? (
                    <textarea
                      {...register(`extra_data.${field.id}`, { required: field.required ? `${field.label} é obrigatório` : false })}
                      disabled={disabled}
                      rows={2}
                      className={`w-full py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        darkMode
                          ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                          : 'border border-gray-300'
                      } ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
                    />
                  ) : field.type === 'date' ? (
                    <Controller
                      name={`extra_data.${field.id}`}
                      control={control}
                      rules={{ required: field.required ? `${field.label} é obrigatório` : false }}
                      render={({ field: f }) => (
                        <input
                          type="text"
                          placeholder="DD/MM/AAAA"
                          value={f.value || ''}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
                            let masked = digits;
                            if (digits.length > 2) masked = digits.slice(0, 2) + '/' + digits.slice(2);
                            if (digits.length > 4) masked = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
                            f.onChange(masked);
                          }}
                          onBlur={f.onBlur}
                          maxLength={10}
                          disabled={disabled}
                          className={`w-full py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                            darkMode
                              ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                              : 'border border-gray-300'
                          } ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
                        />
                      )}
                    />
                  ) : field.type === 'phone' ? (
                    <Controller
                      name={`extra_data.${field.id}`}
                      control={control}
                      rules={{ required: field.required ? `${field.label} é obrigatório` : false }}
                      render={({ field: f }) => (
                        <input
                          type="tel"
                          placeholder="(00) 00000-0000"
                          value={f.value || ''}
                          onChange={(e) => {
                            const d = e.target.value.replace(/\D/g, '').slice(0, 11);
                            let m = d;
                            if (d.length > 2) m = `(${d.slice(0, 2)}) ${d.slice(2)}`;
                            if (d.length > 7) m = `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
                            f.onChange(m);
                          }}
                          onBlur={f.onBlur}
                          maxLength={16}
                          disabled={disabled}
                          className={`w-full py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                            darkMode
                              ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                              : 'border border-gray-300'
                          } ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
                        />
                      )}
                    />
                  ) : (
                    <input
                      type={field.type}
                      {...register(`extra_data.${field.id}`, { required: field.required ? `${field.label} é obrigatório` : false })}
                      disabled={disabled}
                      className={`w-full py-2 px-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        darkMode
                          ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                          : 'border border-gray-300'
                      } ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
                    />
                  )}
                </div>
              );
            }
          })}
      </div>
    </div>
  );
};

export default PersonalInfoSection;
